// src/Root.tsx

import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, legals } from './localization';

import { identifyStrategy, RootState, strategy } from 'ouisys-engine/strategy';

import ProvidersWrapper from './providers/ProvidersWrapper';
import { useRootContext } from './providers/RootContext';
import { useDispatch } from './__doNotModify/store';

import FLOWS from './__doNotModify/includedFlows';

import Loader from './components/Loader';
import UserPaymentStatus from './components/UserPaymentStatus';
import Video from './components/Video';
import Features from './components/Features';
import ContentGrid from './components/ContentGrid';
import CancellationInfo from './components/CancellationInfo';
import ErrorModal from './components/ErrorModal';
import ClearPricing from './components/ClearPricing';
import CreditCardDisclaimer from './components/CreditCardDisclaimer';
import PriceCopy from './components/PriceCopy';
import Menu from './components/Menu';

import useApplePayHandler from './utils/useApplePayHandler';

import { Link } from 'ouisys-component-library';

import './styles/Root.scss';
import GooglePayButton from './components/GooglePayButton';
import useGooglePayHandler from './utils/useGooglePayHandler';

const Root: React.FC = () => {
  const dispatch = useDispatch();
  const { currentState } = useSelector((state: RootState) => state.strategy);
  const {
    isApplePayAvailable,
    setIsApplePayAvailable,
    isGooglePayAvailable,
    setIsGooglePayAvailable,
    userCanMakeGooglePayment,
    setUserCanMakeGooglePayment,
    errorMessage,
    serviceName,
    localePortfolioUrl,
    isLoading,
    setIsLoading,
    isSafari
  } = useRootContext();

  // Utilize the custom Apple Pay handler
  const { handleApplePayClick, userPaymentStatus } = useApplePayHandler({
    setIsApplePayAvailable,
    isApplePayAvailable
  });

  const { handleGooglePayClick } = useGooglePayHandler({
    setIsGooglePayAvailable,
    isGooglePayAvailable,
    setUserCanMakeGooglePayment
  });

  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

  const handleClick = () => {
    if (isLoading) return;
    if (isSafari() || isIOS) {
      setIsLoading(true);
      handleApplePayClick('video');
      return;
    }
    if (userCanMakeGooglePayment) {
      setIsLoading(true);
      handleGooglePayClick('video');
    }
  };

  const identifyStrategyEffect = useCallback(() => {
    dispatch(identifyStrategy());
  }, [dispatch]);

  useEffect(() => {
    identifyStrategyEffect();
    // Any other side effects can be added here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderStrategy = () => {
    return strategy({
      identifyInitialState: () => <Loader />,
      creditCardFlow: () => <FLOWS.CreditCardFlow />
    })(currentState);
  };

  return (
    <div className="page-wrapper">
      {userPaymentStatus ? <UserPaymentStatus /> : ''}

      {/* Payment form modal */}

      <nav>
        <div className={`logo mb-10 ${serviceName}`}>
          <Link href={localePortfolioUrl}>
            <img src={require(`./assets/logos/${serviceName}.svg`)} alt={`${serviceName} logo`} />
          </Link>
        </div>
        <Menu />
      </nav>

      {/* Error Modal */}
      {/* {errorMessage && isApplePayAvailable && <ErrorModal />} */}

      <div className="above-fold">
        <Video onClicked={handleClick} />
        <PriceCopy className="show-desktop mt-10" />
        <button className="btn start-now-button" disabled={!isApplePayAvailable || isLoading} onClick={handleClick}>
          <span className="show-desktop">
            <FormattedMessage id="startNow" defaultMessage="Start Now" />
          </span>
          <span className="show-mobile">
            <FormattedMessage id="watchNow" defaultMessage="Watch Now" />
          </span>
        </button>
      </div>
      {/* <GooglePayButton /> */}
      <ContentGrid className="mt-6" />
      <Features className="mt-10" />
      <ClearPricing className="mt-10 mb-5" />
      <CancellationInfo className="my-6" />

      <footer className="disclaimer">
        <CreditCardDisclaimer />
      </footer>
    </div>
  );
};

const RootWithProviders: React.FC = () => (
  <ProvidersWrapper>
    <Root />
  </ProvidersWrapper>
);

export default RootWithProviders;
